/**
 * Upload Wizard - 3-Step Ontology Upload Flow
 *
 * Step 1: File upload with drag & drop
 * Step 2: Configure metadata (name, namespace, version, description)
 * Step 3: Review parsed terms & activate
 *
 * @version 1.0.0
 * @date 2025-11-28
 */

import React, { useState, useCallback, useRef } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import '../../styles/ontology-management.css';

// =============================================================================
// TYPES
// =============================================================================

interface UploadWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (ontologyId: string) => void;
}

interface OntologyMetadata {
  name: string;
  namespace: string;
  version: string;
  description: string;
  source_url: string;
  license: string;
  access_level: 'organization' | 'private' | 'shared';
}

interface _ParsedOntologyPreview {
  term_count: number;
  class_count: number;
  property_count: number;
  individual_count: number;
  hierarchy_depth: number;
  sample_classes: string[];
  sample_properties: string[];
}

interface UploadResponse {
  ontology_id: string;
  name: string;
  namespace: string;
  version: string;
  status: string;
  statistics: {
    term_count: number;
    class_count: number;
    property_count: number;
    individual_count: number;
    hierarchy_depth: number;
    max_breadth: number;
    triple_count: number;
    namespace_count: number;
  };
  created_at: string;
  message: string;
}

type WizardStep = 1 | 2 | 3;

// =============================================================================
// STEP INDICATOR COMPONENT
// =============================================================================

const StepIndicator: React.FC<{ currentStep: WizardStep }> = ({ currentStep }) => {
  const steps = [
    { num: 1, label: 'Upload File' },
    { num: 2, label: 'Configure' },
    { num: 3, label: 'Review' },
  ];

  return (
    <div className="wizard-steps">
      {steps.map((step, index) => (
        <React.Fragment key={step.num}>
          <div
            className={`wizard-step ${
              currentStep === step.num
                ? 'step-active'
                : currentStep > step.num
                ? 'step-complete'
                : ''
            }`}
          >
            <div className="step-number">
              {currentStep > step.num ? '✓' : step.num}
            </div>
            <div className="step-label">{step.label}</div>
          </div>
          {index < steps.length - 1 && (
            <div
              className={`step-connector ${
                currentStep > step.num ? 'connector-complete' : ''
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// =============================================================================
// STEP 1: FILE UPLOAD
// =============================================================================

interface Step1Props {
  file: File | null;
  onFileSelect: (file: File) => void;
  onNext: () => void;
}

const Step1FileUpload: React.FC<Step1Props> = ({ file, onFileSelect, onNext }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        onFileSelect(files[0]);
      }
    },
    [onFileSelect]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        onFileSelect(files[0]);
      }
    },
    [onFileSelect]
  );

  const formatFileSize = (bytes: number): string => {
    if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }
    if (bytes >= 1024) {
      return `${(bytes / 1024).toFixed(2)} KB`;
    }
    return `${bytes} bytes`;
  };

  const getFormatIcon = (filename: string): string => {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'owl':
      case 'rdf':
      case 'xml':
        return '🦉';
      case 'ttl':
        return '🐢';
      case 'jsonld':
      case 'json':
        return '📋';
      default:
        return '📄';
    }
  };

  return (
    <div className="wizard-step-content">
      <h2>Upload Ontology File</h2>
      <p className="step-description">
        Drag and drop your ontology file, or click to browse. Supported formats:
        OWL, Turtle (.ttl), JSON-LD, RDF/XML.
      </p>

      <div
        className={`upload-dropzone ${isDragging ? 'dropzone-active' : ''} ${
          file ? 'dropzone-has-file' : ''
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          type="file"
          ref={fileInputRef}
          accept=".owl,.rdf,.xml,.ttl,.jsonld,.json,.nt"
          onChange={handleFileInput}
          className="hidden-input"
        />

        {!file ? (
          <>
            <div className="dropzone-icon">📁</div>
            <div className="dropzone-text">
              <span className="dropzone-primary">
                Drag & drop your ontology file here
              </span>
              <span className="dropzone-secondary">or click to browse</span>
            </div>
            <div className="dropzone-formats">
              Supported: .owl, .ttl, .jsonld, .rdf, .xml (max 50 MB)
            </div>
          </>
        ) : (
          <div className="file-preview">
            <div className="file-icon">{getFormatIcon(file.name)}</div>
            <div className="file-details">
              <span className="file-name">{file.name}</span>
              <span className="file-size">{formatFileSize(file.size)}</span>
            </div>
            <button
              className="file-remove"
              onClick={(e) => {
                e.stopPropagation();
                onFileSelect(null as unknown as File);
              }}
              aria-label="Remove file"
            >
              ✕
            </button>
          </div>
        )}
      </div>

      <div className="step-actions">
        <button className="btn btn-primary" disabled={!file} onClick={onNext}>
          Next: Configure Metadata
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// STEP 2: METADATA CONFIGURATION
// =============================================================================

interface Step2Props {
  metadata: OntologyMetadata;
  filename: string;
  onChange: (metadata: OntologyMetadata) => void;
  onBack: () => void;
  onNext: () => void;
}

const Step2Configuration: React.FC<Step2Props> = ({
  metadata,
  filename,
  onChange,
  onBack,
  onNext,
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Auto-suggest name and namespace from filename
  React.useEffect(() => {
    if (!metadata.name && filename) {
      const baseName = filename.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ');
      const capitalizedName = baseName
        .split(' ')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
      onChange({ ...metadata, name: capitalizedName });
    }

    if (!metadata.namespace && filename) {
      const namespace = filename
        .replace(/\.[^/.]+$/, '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '_')
        .replace(/^[0-9]/, 'ont_');
      onChange({ ...metadata, namespace });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Initial auto-fill only when filename changes
  }, [filename]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    onChange({ ...metadata, [name]: value });

    // Clear error when user types
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!metadata.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!metadata.namespace.trim()) {
      newErrors.namespace = 'Namespace is required';
    } else if (!/^[a-z][a-z0-9_-]*$/.test(metadata.namespace)) {
      newErrors.namespace =
        'Namespace must start with a letter and contain only lowercase letters, numbers, hyphens, and underscores';
    }

    if (!metadata.version.trim()) {
      newErrors.version = 'Version is required';
    } else if (!/^\d+\.\d+(\.\d+)?$/.test(metadata.version)) {
      newErrors.version = 'Version must be in format X.Y or X.Y.Z';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      onNext();
    }
  };

  return (
    <div className="wizard-step-content">
      <h2>Configure Ontology</h2>
      <p className="step-description">
        Provide metadata for your ontology. This information will be used to
        identify and categorize it.
      </p>

      <div className="form-grid">
        <div className="form-group">
          <label htmlFor="name">
            Display Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={metadata.name}
            onChange={handleChange}
            placeholder="e.g., Amgen Data Dictionary"
            className={errors.name ? 'input-error' : ''}
          />
          {errors.name && <span className="error-text">{errors.name}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="namespace">
            Namespace <span className="required">*</span>
          </label>
          <input
            type="text"
            id="namespace"
            name="namespace"
            value={metadata.namespace}
            onChange={handleChange}
            placeholder="e.g., amgen_dd"
            className={errors.namespace ? 'input-error' : ''}
          />
          <span className="form-hint">
            Unique identifier prefix (lowercase, no spaces)
          </span>
          {errors.namespace && <span className="error-text">{errors.namespace}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="version">
            Version <span className="required">*</span>
          </label>
          <input
            type="text"
            id="version"
            name="version"
            value={metadata.version}
            onChange={handleChange}
            placeholder="e.g., 1.0.0"
            className={errors.version ? 'input-error' : ''}
          />
          {errors.version && <span className="error-text">{errors.version}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="access_level">Access Level</label>
          <select
            id="access_level"
            name="access_level"
            value={metadata.access_level}
            onChange={handleChange}
          >
            <option value="organization">Organization (all users)</option>
            <option value="private">Private (only me)</option>
            <option value="shared">Shared (specific users)</option>
          </select>
        </div>

        <div className="form-group form-group-full">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={metadata.description}
            onChange={handleChange}
            placeholder="Describe the purpose and scope of this ontology..."
            rows={3}
          />
        </div>

        <div className="form-group">
          <label htmlFor="source_url">Source URL (optional)</label>
          <input
            type="url"
            id="source_url"
            name="source_url"
            value={metadata.source_url}
            onChange={handleChange}
            placeholder="https://example.com/ontology"
          />
        </div>

        <div className="form-group">
          <label htmlFor="license">License (optional)</label>
          <input
            type="text"
            id="license"
            name="license"
            value={metadata.license}
            onChange={handleChange}
            placeholder="e.g., CC-BY-4.0, MIT, Proprietary"
          />
        </div>
      </div>

      <div className="step-actions">
        <button className="btn btn-secondary" onClick={onBack}>
          Back
        </button>
        <button className="btn btn-primary" onClick={handleNext}>
          Next: Review & Upload
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// STEP 3: REVIEW & UPLOAD
// =============================================================================

interface Step3Props {
  file: File;
  metadata: OntologyMetadata;
  onBack: () => void;
  onUpload: () => void;
  uploading: boolean;
  uploadProgress: number;
  uploadResult: UploadResponse | null;
  uploadError: string | null;
  onDeploy: () => void;
  onFinish: () => void;
}

const Step3Review: React.FC<Step3Props> = ({
  file,
  metadata,
  onBack,
  onUpload,
  uploading,
  uploadProgress,
  uploadResult,
  uploadError,
  onDeploy,
  onFinish,
}) => {
  const formatFileSize = (bytes: number): string => {
    if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }
    return `${(bytes / 1024).toFixed(2)} KB`;
  };

  // Upload in progress
  if (uploading) {
    return (
      <div className="wizard-step-content">
        <h2>Uploading & Parsing</h2>
        <div className="upload-progress">
          <div className="progress-spinner" />
          <p className="progress-text">
            {uploadProgress < 50
              ? 'Uploading file...'
              : uploadProgress < 80
              ? 'Parsing ontology...'
              : 'Building term index...'}
          </p>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <span className="progress-percent">{uploadProgress}%</span>
        </div>
      </div>
    );
  }

  // Upload complete
  if (uploadResult) {
    return (
      <div className="wizard-step-content">
        <h2>Upload Complete!</h2>

        <div className="upload-success">
          <div className="success-icon">✓</div>
          <h3>{uploadResult.name}</h3>
          <p className="success-namespace">
            {uploadResult.namespace}:{uploadResult.version}
          </p>
        </div>

        <div className="upload-stats">
          <div className="stat-card">
            <span className="stat-value">{uploadResult.statistics.term_count}</span>
            <span className="stat-label">Total Terms</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{uploadResult.statistics.class_count}</span>
            <span className="stat-label">Classes</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{uploadResult.statistics.property_count}</span>
            <span className="stat-label">Properties</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">
              {uploadResult.statistics.individual_count}
            </span>
            <span className="stat-label">Individuals</span>
          </div>
        </div>

        <div className="upload-status-message">
          <span className="status-draft-badge">Draft</span>
          <p>
            Your ontology has been uploaded and is in <strong>draft</strong> status.
            Deploy it to make it active for validation.
          </p>
        </div>

        <div className="step-actions">
          <button className="btn btn-secondary" onClick={onFinish}>
            Close
          </button>
          <button className="btn btn-primary" onClick={onDeploy}>
            Deploy Now
          </button>
        </div>
      </div>
    );
  }

  // Upload error
  if (uploadError) {
    return (
      <div className="wizard-step-content">
        <h2>Upload Failed</h2>

        <div className="upload-error">
          <div className="error-icon">✕</div>
          <h3>Something went wrong</h3>
          <p className="error-message">{uploadError}</p>
        </div>

        <div className="step-actions">
          <button className="btn btn-secondary" onClick={onBack}>
            Back to Configure
          </button>
          <button className="btn btn-primary" onClick={onUpload}>
            Retry Upload
          </button>
        </div>
      </div>
    );
  }

  // Review before upload
  return (
    <div className="wizard-step-content">
      <h2>Review & Upload</h2>
      <p className="step-description">
        Please review your ontology configuration before uploading.
      </p>

      <div className="review-section">
        <h3>File Information</h3>
        <div className="review-grid">
          <div className="review-item">
            <span className="review-label">File Name</span>
            <span className="review-value">{file.name}</span>
          </div>
          <div className="review-item">
            <span className="review-label">File Size</span>
            <span className="review-value">{formatFileSize(file.size)}</span>
          </div>
        </div>
      </div>

      <div className="review-section">
        <h3>Ontology Metadata</h3>
        <div className="review-grid">
          <div className="review-item">
            <span className="review-label">Name</span>
            <span className="review-value">{metadata.name}</span>
          </div>
          <div className="review-item">
            <span className="review-label">Namespace</span>
            <span className="review-value">{metadata.namespace}</span>
          </div>
          <div className="review-item">
            <span className="review-label">Version</span>
            <span className="review-value">{metadata.version}</span>
          </div>
          <div className="review-item">
            <span className="review-label">Access Level</span>
            <span className="review-value">
              {metadata.access_level === 'organization'
                ? 'Organization-wide'
                : metadata.access_level === 'private'
                ? 'Private'
                : 'Shared'}
            </span>
          </div>
          {metadata.description && (
            <div className="review-item review-item-full">
              <span className="review-label">Description</span>
              <span className="review-value">{metadata.description}</span>
            </div>
          )}
          {metadata.license && (
            <div className="review-item">
              <span className="review-label">License</span>
              <span className="review-value">{metadata.license}</span>
            </div>
          )}
        </div>
      </div>

      <div className="step-actions">
        <button className="btn btn-secondary" onClick={onBack}>
          Back
        </button>
        <button className="btn btn-primary" onClick={onUpload}>
          Upload Ontology
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// ERROR READING
// =============================================================================

/**
 * Pull the human-readable reason out of a failed response.
 *
 * The API's standard failure envelope is `{ success, error: { code, message } }`
 * (server/middleware/errorHandler), while a few older routes answer with a flat
 * `{ error, message }`. Reading only the flat shape silently discarded every
 * classified reason and showed a bare "Upload failed" instead — which is how a
 * plain "unsupported file extension" became undiagnosable (2026-08-11).
 *
 * A non-JSON body (proxy/gateway HTML, or a connection cut mid-response) must
 * not throw a `SyntaxError` over the real failure either.
 */
async function readErrorMessage(response: Response, fallback: string): Promise<string> {
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    return `${fallback} (HTTP ${response.status})`;
  }

  const body = payload as {
    error?: string | { message?: string };
    message?: string;
  } | null;

  const nested =
    body?.error && typeof body.error === 'object' ? body.error.message : undefined;
  const flatError = typeof body?.error === 'string' ? body.error : undefined;

  return nested || body?.message || flatError || `${fallback} (HTTP ${response.status})`;
}

// =============================================================================
// MAIN WIZARD COMPONENT
// =============================================================================

export const UploadWizard: React.FC<UploadWizardProps> = ({
  isOpen,
  onClose,
  onComplete,
}) => {
  const { authFetch } = useApiClient();
  // State
  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [file, setFile] = useState<File | null>(null);
  const [metadata, setMetadata] = useState<OntologyMetadata>({
    name: '',
    namespace: '',
    version: '1.0.0',
    description: '',
    source_url: '',
    license: '',
    access_level: 'organization',
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState<UploadResponse | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Reset state when wizard closes
  React.useEffect(() => {
    if (!isOpen) {
      setCurrentStep(1);
      setFile(null);
      setMetadata({
        name: '',
        namespace: '',
        version: '1.0.0',
        description: '',
        source_url: '',
        license: '',
        access_level: 'organization',
      });
      setUploading(false);
      setUploadProgress(0);
      setUploadResult(null);
      setUploadError(null);
    }
  }, [isOpen]);

  // Handle upload
  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setUploadProgress(10);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', metadata.name);
      formData.append('namespace', metadata.namespace);
      formData.append('version', metadata.version);
      formData.append('description', metadata.description);
      formData.append('access_level', metadata.access_level);
      if (metadata.source_url) {
        formData.append('source_url', metadata.source_url);
      }
      if (metadata.license) {
        formData.append('license', metadata.license);
      }

      setUploadProgress(30);

      const response = await authFetch('/api/ontologies/upload', {
        method: 'POST',
        body: formData,
      });

      setUploadProgress(70);

      if (!response.ok) {
        throw new Error(await readErrorMessage(response, 'Upload failed'));
      }

      const result: UploadResponse = await response.json();
      setUploadProgress(100);
      setUploadResult(result);

      console.log('[UploadWizard] Upload successful:', result);
    } catch (err: unknown) {
      console.error('[UploadWizard] Upload error:', err);
      setUploadError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setUploading(false);
    }
  };

  // Handle deploy
  const handleDeploy = async () => {
    if (!uploadResult) return;

    try {
      const response = await authFetch(
        `/api/ontologies/${uploadResult.ontology_id}/deploy`,
        {
          method: 'PATCH',
        }
      );

      if (!response.ok) {
        throw new Error(await readErrorMessage(response, 'Deploy failed'));
      }

      console.log('[UploadWizard] Deploy successful');
      onComplete(uploadResult.ontology_id);
    } catch (err: unknown) {
      console.error('[UploadWizard] Deploy error:', err);
      alert(`Failed to deploy: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // Handle finish (without deploy)
  const handleFinish = () => {
    if (uploadResult) {
      onComplete(uploadResult.ontology_id);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="wizard-overlay">
      <div className="wizard-modal">
        <div className="wizard-header">
          <h1>Upload Private Ontology</h1>
          <button className="wizard-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <StepIndicator currentStep={currentStep} />

        <div className="wizard-body">
          {currentStep === 1 && (
            <Step1FileUpload
              file={file}
              onFileSelect={setFile}
              onNext={() => setCurrentStep(2)}
            />
          )}

          {currentStep === 2 && (
            <Step2Configuration
              metadata={metadata}
              filename={file?.name || ''}
              onChange={setMetadata}
              onBack={() => setCurrentStep(1)}
              onNext={() => setCurrentStep(3)}
            />
          )}

          {currentStep === 3 && file && (
            <Step3Review
              file={file}
              metadata={metadata}
              onBack={() => setCurrentStep(2)}
              onUpload={handleUpload}
              uploading={uploading}
              uploadProgress={uploadProgress}
              uploadResult={uploadResult}
              uploadError={uploadError}
              onDeploy={handleDeploy}
              onFinish={handleFinish}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadWizard;
